import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);


/**
 * Authentication Context Provider.
 * Manages user state, login, OTP verification, logout, and token persistence in localStorage.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - Child elements wrapping the provider.
 * @returns {JSX.Element} AuthContext Provider component.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore user session from localStorage on initial load
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          setUser(parsedUser);
        } else {
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (err) {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  /**
   * Authenticate user with email and password.
   *
   * @async
   * @function login
   * @param {string} email - User email address.
   * @param {string} password - User password.
   * @returns {Promise<Object>} Authenticated user object or API response.
   */
  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const token = res.data?.data?.token || res.data?.token;
    const userObj = res.data?.data?.user || res.data?.data;

    if (token && userObj) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userObj);
    }
    return res.data;
  };

  /**
   * Verify 2FA OTP code and complete user login.
   *
   * @async
   * @function verifyOtpAndLogin
   * @param {string} email - User email address.
   * @param {string} otp - One-time password verification code.
   * @returns {Promise<Object>} Authenticated user object.
   */
  const verifyOtpAndLogin = async (email, otp) => {
    const res = await api.post('/auth/verify-otp', { email, otp });
    const token = res.data?.data?.token || res.data?.token;
    const userObj = res.data?.data?.user || res.data?.data || res.data;

    if (token && userObj) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userObj);
    }
    return userObj;
  };

  /**
   * Log out current user, purge storage, and clear Axios authorization headers.
   *
   * @function logout
   * @returns {void}
   */
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyOtpAndLogin, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom React hook to consume the Auth Context.
 *
 * @function useAuth
 * @returns {Object} Auth context state and actions ({ user, login, verifyOtpAndLogin, logout, loading }).
 * @throws {Error} If called outside of an AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
